import { Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import SectionHeading from '@/components/feature/SectionHeading';
import { categories } from '@/mocks/categories';

const values = [
  {
    icon: 'ri-flashlight-line',
    title: '快',
    desc: '紧跟热点节奏，重要事件第一时间整理成可读的资讯。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '准',
    desc: '区分事实与讨论，注明来源，不制造未经证实的结论。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '有趣',
    desc: '把信息写得轻松好读，让吃瓜也能是件舒服的事。',
  },
];

const team = [
  { name: '沈知远', role: '内容主编', initial: '沈', bio: '十年文娱报道经验，负责选题与内容把关。' },
  { name: '陆一禾', role: '影视编辑', initial: '陆', bio: '专注剧集与电影领域，追剧速度全组第一。' },
  { name: '江沐', role: '综艺编辑', initial: '江', bio: '深度综艺观众，擅长梳理解读节目逻辑。' },
  { name: '祁然', role: '热点追踪', initial: '祁', bio: '盯热搜、理时间线，还原事件来龙去脉。' },
];

const guide = [
  { step: '01', title: '浏览首页', desc: '首页汇总焦点大图、最新新闻与热门榜单，快速掌握今日风向。' },
  { step: '02', title: '进入分类', desc: '左侧频道导航可切换娱乐新闻、明星动态与网络热点，支持排序与标签筛选。' },
  { step: '03', title: '搜索内容', desc: '顶部搜索框输入关键词，即可匹配标题、简介与标签。' },
  { step: '04', title: '收藏分享', desc: '在详情页可收藏文章（保存在本设备）或分享给朋友。' },
];

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们 - 91吃瓜',
    description: '了解 91吃瓜 的网站定位与运营理念，认识内容团队，查看联系方式与用户指南。',
    keywords: '91吃瓜,关于我们,娱乐资讯团队,联系方式',
  });

  return (
    <div className="w-full">
      <section className="bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-primary-600 text-sm font-medium">
              <span className="w-6 h-[2px] bg-primary-500" />
              关于我们
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950 leading-tight">
              91吃瓜，认真对待每一次吃瓜
            </h1>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600 max-w-xl">
              我们是一支专注中文娱乐资讯的小团队。每天，互联网上会产生大量与影视、综艺、明星和热点相关的信息，
              我们做的事情，就是把这些碎片重新整理成一条清晰的时间线、一段好读的叙述，
              让关心娱乐的人不必在嘈杂的信息里反复翻找。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                联系我们
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-50 text-foreground-800 border border-background-300 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览娱乐新闻
              </Link>
            </div>
          </div>
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20cozy%20modern%20newsroom%20with%20warm%20orange%20evening%20light%2C%20desks%20and%20screens%20glowing%2C%20charcoal%20and%20amber%20palette%2C%20soft%20grain%2C%20no%20text&width=800&height=600&seq=about-01&orientation=landscape"
              alt="91吃瓜 内容团队工作场景"
              title="91吃瓜 内容团队 - 91吃瓜"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <SectionHeading
          title="网站定位"
          subtitle="我们服务的，是所有对娱乐圈感兴趣的人"
          icon="ri-compass-3-line"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {categories.map((cat) => (
            <div key={cat.key} className="rounded-lg bg-background-50 border border-background-200 p-5">
              <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className={`${cat.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{cat.name}</h3>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{cat.description}</p>
              <Link
                to={cat.path}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                进入频道
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <SectionHeading title="运营理念" subtitle="三个我们一直在坚持的关键词" icon="ri-quill-pen-line" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <span className="w-12 h-12 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading font-black text-xl text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <SectionHeading title="内容团队" subtitle="一群把娱乐当正经事在做的人" icon="ri-team-line" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {team.map((member) => (
            <div key={member.name} className="rounded-lg bg-background-50 border border-background-200 p-5 text-center">
              <span className="w-16 h-16 mx-auto rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-heading font-black text-2xl">
                {member.initial}
              </span>
              <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{member.name}</h3>
              <p className="mt-1 text-xs text-primary-600 font-medium">{member.role}</p>
              <p className="mt-3 text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <SectionHeading title="用户指南" subtitle="四步就能用起来" icon="ri-book-open-line" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {guide.map((item) => (
              <div key={item.step} className="rounded-lg bg-background-50 border border-background-200 p-5">
                <span className="font-heading font-black text-2xl text-primary-300">{item.step}</span>
                <h3 className="mt-3 font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="rounded-lg bg-primary-950 p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading font-black text-2xl md:text-3xl text-background-50">
              有内容想推荐，或想和我们合作？
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-200/80 leading-relaxed">
              欢迎随时通过邮件或社交媒体找到我们。读者的线索，往往是最好的选题来源。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                填写联系表单
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50/10">
              <i className="ri-mail-line text-xl text-primary-300" />
              <span className="text-sm text-background-100">hello@chigua91.demo</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50/10">
              <i className="ri-weibo-line text-xl text-primary-300" />
              <span className="text-sm text-background-100">@91吃瓜</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50/10">
              <i className="ri-map-pin-line text-xl text-primary-300" />
              <span className="text-sm text-background-100">内容中心 · 线上协作</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}